import React, { useState, useRef, useEffect, useMemo } from 'react';
import './Trade.css';
import CondensedTradeCard from '../../common/Card/CondensedTradeCard';
import SearchFilterBar from '../../common/SearchFilterBar/SearchFilterBar';
import { useUser } from '../../auth/AuthPage/UserContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { getProfilePhotoSrc, PLACEHOLDER_DATA_URI } from '../../../utils/profilePhoto';
import AvatarFrame from '../../common/AvatarFrame/AvatarFrame';
import LoginGate from '../../common/LoginGate/LoginGate';
import TraderMatch from '../../common/TraderMatch/TraderMatch';

// Offers always go to someone, so /trade with no partner asks who -
// with the traders whose binders fit yours right there to pick from
function PartnerPicker() {
    const navigate = useNavigate();
    const [fits, setFits] = useState(null);

    useEffect(() => {
        fetch('/api/suggested/users', { credentials: 'include' })
            .then(r => (r.ok ? r.json() : { users: [] }))
            .then(d => setFits(d.users || []))
            .catch(() => setFits([]));
    }, []);

    return (
        <div className="requests-container">
            <header className="page-section-head">
                <h1>Trade <span className="page-section-current">New offer</span></h1>
            </header>
            <div className="trade-picker">
                <div className="trade-picker-head">
                    <h2>Who's it for?</h2>
                    <button className="os-btn ghost" onClick={() => navigate('/traders')}>Search all traders →</button>
                </div>
                {fits === null ? (
                    <p className="os-hint">Finding traders&hellip;</p>
                ) : fits.length === 0 ? (
                    <div className="requests-placeholder">
                        <p style={{ margin: '0 0 0.75rem' }}>
                            No binder fits yet. Add cards to your binder and wishlist, or find anyone by name.
                        </p>
                        <button className="cta-gold" onClick={() => navigate('/traders')}>Find traders</button>
                    </div>
                ) : (
                    <div className="os-match-grid">
                        {fits.slice(0, 8).map(m => <TraderMatch key={m.id} m={m} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function Trade() {
    const { user, loading: userLoading } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const requestData = location.state?.requestData;
    const ownerId = location.state?.ownerId;
    const editData = location.state?.editData;
    const isEditing = location.state?.isEditing;
    const isCounterOffer = location.state?.isCounterOffer;

    // Trade partner state
    const [tradePartner, setTradePartner] = useState(null);
    const [tradePartnerWants, setTradePartnerWants] = useState([]);
    const [myWants, setMyWants] = useState([]);
    const [showMatches, setShowMatches] = useState(false);

    // Toggle state - true = My Cards (filter mode), false = All Cards (search mode)
    const [toggleOn, setToggleOn] = useState(true);
    // Query carried across the my-cards -> all-cards switch
    const [pendingSearch, setPendingSearch] = useState(null);
    const [activeTab, setActiveTab] = useState('have');

    // Collection cards state
    const [cards, setCards] = useState([]);
    const [cardsCache, setCardsCache] = useState({ have: [], want: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Trade draft state
    const [leftTradeCards, setLeftTradeCards] = useState([]);
    const [rightTradeCards, setRightTradeCards] = useState([]);

    // Note state
    const [noteText, setNoteText] = useState(editData?.note || '');
    const [showNotePopup, setShowNotePopup] = useState(false);
    const [noteCharCount, setNoteCharCount] = useState(editData?.note?.length || 0);

    // Submit state
    const [submitLoading, setSubmitLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    // Set when the partner's binder is off-limits (private, suspended,
    // deleted): the server's reason, shown instead of the builder
    const [partnerBlocked, setPartnerBlocked] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);

    const controlsBarRef = useRef(null);
    const searchAreaRef = useRef(null);

    // Handle placeholder click - switch tab and scroll to search area
    const handlePlaceholderClick = (tab) => {
        setActiveTab(tab);
        setTimeout(() => {
            searchAreaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    // Force toggleOn to true when on want tab with ownerId (hide toggle switch)
    const effectiveToggleOn = activeTab === 'want' && ownerId ? true : toggleOn;

    // Fetch trade partner info and wants if ownerId is provided
    useEffect(() => {
        async function fetchTradePartner() {
            if (!ownerId) {
                setTradePartner(null);
                setTradePartnerWants([]);
                return;
            }
            try {
                // Fetch profile
                const profileResponse = await fetch(`/api/users/${ownerId}/profile`, {
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' }
                });
                if (profileResponse.ok) {
                    const userData = await profileResponse.json();
                    setTradePartner(userData);
                }

                // Fetch their wants. A 403 means their binder is off-limits
                // (private, suspended, deleted) and no trade can be sent.
                const wantsResponse = await fetch(`/api/wants/${ownerId}`, {
                    credentials: 'include'
                });
                if (wantsResponse.status === 403) {
                    const data = await wantsResponse.json().catch(() => ({}));
                    setPartnerBlocked(data.error || 'This trader keeps their binders private.');
                    setTradePartnerWants([]);
                    return;
                }
                setPartnerBlocked(null);
                if (wantsResponse.ok) {
                    const wantsData = await wantsResponse.json();
                    setTradePartnerWants(wantsData.wants || []);
                }

                // Fetch current user's wants (for matching on Their Collection tab)
                const myWantsResponse = await fetch('/api/wants', {
                    credentials: 'include'
                });
                if (myWantsResponse.ok) {
                    const myWantsData = await myWantsResponse.json();
                    setMyWants(myWantsData.wants || []);
                }
            } catch (err) {
                console.error('Error fetching trade partner info:', err);
                setTradePartner(null);
                setTradePartnerWants([]);
            }
        }
        fetchTradePartner();
    }, [ownerId]);

    // Fetch tab data function
    const fetchTabData = async (tab) => {
        if (!user) return [];

        // If we're on the "want" tab and have an ownerId, fetch the owner's collection
        if (tab === 'want' && ownerId) {
            const res = await fetch(`/api/haves/${ownerId}`, { credentials: 'include' });
            if (!res.ok) throw new Error("Failed to fetch owner's collection.");
            const data = await res.json();
            const list = data.haves || [];
            if (!list || list.length === 0) return [];

            const entries = list.map(row => ({ uuid: row.uuid, setcode: row.setcode }));
            const cardRes = await fetch('/api/cards/by-uuids-with-setcodes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ entries })
            });
            if (!cardRes.ok) throw new Error('Failed to fetch card details.');
            const cardData = await cardRes.json();
            return list.map(entry => {
                const card = cardData.cards.find(cardObj => cardObj.uuid === entry.uuid);
                return { card, binder: entry };
            });
        } else {
            // Normal flow for current user's cards
            const endpoint = tab === 'have' ? '/api/haves' : '/api/wants';
            const res = await fetch(endpoint, { credentials: 'include' });
            if (!res.ok) throw new Error('Failed to fetch your cards.');
            const data = await res.json();
            const list = tab === 'have' ? data.haves : data.wants;
            if (!list || list.length === 0) return [];

            const entries = list.map(row => ({ uuid: row.uuid, setcode: row.setcode }));
            const cardRes = await fetch('/api/cards/by-uuids-with-setcodes', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ entries })
            });
            if (!cardRes.ok) throw new Error('Failed to fetch card details.');
            const cardData = await cardRes.json();
            return list.map(entry => {
                const card = cardData.cards.find(cardObj => cardObj.uuid === entry.uuid);
                return { card, binder: entry };
            });
        }
    };

    // On mount, fetch both tabs in parallel
    useEffect(() => {
        if (!user) return;
        setLoading(true);
        setError(null);
        Promise.all([fetchTabData('have'), fetchTabData('want')])
            .then(([haveData, wantData]) => {
                setCardsCache({ have: haveData, want: wantData });
                setCards(activeTab === 'have' ? haveData : wantData);
            })
            .catch(err => {
                setError(err.message || 'Failed to load your cards.');
                setCardsCache({ have: [], want: [] });
                setCards([]);
            })
            .finally(() => setLoading(false));
    }, [user]);

    // On tab change, use cached data
    useEffect(() => {
        if (cardsCache[activeTab]) {
            setCards(cardsCache[activeTab]);
        }
    }, [activeTab, cardsCache]);

    // Get unique sets from current cards for filter dropdown
    const availableSetsForCards = useMemo(() => {
        const setMap = {};
        cards.forEach(({ card }) => {
            if (card && card.setCode) {
                setMap[card.setCode] = card.setName || card.setCode;
            }
        });
        return Object.keys(setMap);
    }, [cards]);

    // Get matched card UUIDs for highlighting in SearchFilterBar
    const matchedCardKeys = useMemo(() => {
        if (!showMatches || !ownerId) return new Set();

        const keys = new Set();
        if (activeTab === 'have') {
            // On "Your Collection" tab, highlight cards that are in partner's wants
            tradePartnerWants.forEach(want => {
                keys.add(`${want.uuid}-${want.setcode}`);
            });
        } else if (activeTab === 'want') {
            // On "Their Collection" tab, highlight cards that are in my wants
            myWants.forEach(want => {
                keys.add(`${want.uuid}-${want.setcode}`);
            });
        }
        return keys;
    }, [showMatches, ownerId, activeTab, tradePartnerWants, myWants]);

    // Initialize trade cards from request data
    useEffect(() => {
        const initCards = async () => {
            if (requestData) {
                const ownerTrade = Array.isArray(requestData.ownerTrade) ? requestData.ownerTrade : JSON.parse(requestData.ownerTrade);
                const otherTrade = Array.isArray(requestData.otherTrade) ? requestData.otherTrade : JSON.parse(requestData.otherTrade);

                const fetchFullCardData = async (cardData) => {
                    try {
                        const response = await fetch(`/api/cards/${cardData.setCode}/${cardData.uuid}`);
                        if (response.ok) return await response.json();
                    } catch (error) {
                        console.error('Error fetching card data:', error);
                    }
                    return cardData;
                };

                // SWAP: ownerTrade (Have) goes to Want side, otherTrade (Want) goes to Have side
                const leftCards = await Promise.all(
                    otherTrade.map(async (cardData) => {
                        const fullCardData = await fetchFullCardData(cardData);
                        return { card: fullCardData, entry: { finish: cardData.finish || 'nonfoil', setcode: cardData.setCode } };
                    })
                );
                const rightCards = await Promise.all(
                    ownerTrade.map(async (cardData) => {
                        const fullCardData = await fetchFullCardData(cardData);
                        return { card: fullCardData, entry: { finish: cardData.finish || 'nonfoil', setcode: cardData.setCode } };
                    })
                );
                setLeftTradeCards(leftCards);
                setRightTradeCards(rightCards);
            } else if (editData) {
                setLeftTradeCards(editData.leftTradeCards || []);
                setRightTradeCards(editData.rightTradeCards || []);
            }
        };
        initCards();
    }, [requestData, editData]);

    // How many copies of this exact binder entry are already in a draft
    const draftCount = (list, card, entry) => list.filter(tc =>
        tc.card.uuid === card.uuid &&
        (tc.entry?.finish || null) === (entry?.finish || null)).length;

    // Handler to add a card to the trade draft (from collection - has
    // binder entry). Capped at the owned quantity: you can't ask for 10
    // copies of a card someone owns once.
    const handleAddToTrade = (card, entry) => {
        const cap = entry?.quantity || 1;
        const apply = prev => {
            if (prev.length >= 10) return prev;
            if (entry && draftCount(prev, card, entry) >= cap) return prev;
            return [...prev, { card, entry }];
        };
        if (activeTab === 'have') {
            setLeftTradeCards(apply);
        } else {
            setRightTradeCards(apply);
        }
    };

    // Handler to add a card with finish selection (from search results)
    const handleAddToTradeWithFinish = (card, finish) => {
        const entry = { finish, prices: card.prices };
        if (activeTab === 'have') {
            setLeftTradeCards(prev => {
                if (prev.length >= 10) return prev;
                // Replace "any" finish card with same name
                const existingAnyIndex = prev.findIndex(tc => tc.card.name === card.name && tc.entry.finish === 'any');
                if (existingAnyIndex !== -1) {
                    const newCards = [...prev];
                    newCards[existingAnyIndex] = { card, entry };
                    return newCards;
                }
                return [...prev, { card, entry }];
            });
        } else {
            setRightTradeCards(prev => {
                if (prev.length >= 10) return prev;
                const existingAnyIndex = prev.findIndex(tc => tc.card.name === card.name && tc.entry.finish === 'any');
                if (existingAnyIndex !== -1) {
                    const newCards = [...prev];
                    newCards[existingAnyIndex] = { card, entry };
                    return newCards;
                }
                return [...prev, { card, entry }];
            });
        }
    };

    // Handler for "any" finish card button - switches to search mode
    const handleAnyFinishCardClick = (cardName) => {
        setActiveTab('have');
        setToggleOn(false);
        // Note: SearchFilterBar will auto-search when filters change
    };

    // Check if a card is a match with trade partner
    const isMatchedCard = (cardItem) => {
        if (!showMatches || !ownerId) return false;
        const card = cardItem.card;

        if (activeTab === 'have') {
            if (!tradePartnerWants.length) return false;
            return tradePartnerWants.some(want => want.uuid === card.uuid && want.setcode === card.setCode);
        } else if (activeTab === 'want') {
            if (!myWants.length) return false;
            return myWants.some(want => want.uuid === card.uuid && want.setcode === card.setCode);
        }
        return false;
    };

    // Note handlers
    const handleNoteChange = (e) => {
        const text = e.target.value;
        if (text.length <= 250) {
            setNoteText(text);
            setNoteCharCount(text.length);
        }
    };

    // Check if submit button should be disabled
    const isSubmitDisabled = () => {
        const hasAnyFinishInHave = leftTradeCards.some(item => item.entry?.finish === 'any');
        return hasAnyFinishInHave;
    };

    // Submit handler
    const handleSubmitRequest = async () => {
        if (!user) {
            setShowLoginPopup(true);
            return;
        }
        setSubmitLoading(true);
        setSubmitError(null);
        try {
            const ownerTrade = leftTradeCards.map(({ card, entry }) => ({
                uuid: card.uuid,
                setCode: card.setCode,
                name: card.name,
                finish: entry?.finish || (card.finishes?.[0] || 'nonfoil'),
                game: 'magic',
            }));
            const otherTrade = rightTradeCards.map(({ card, entry }) => ({
                uuid: card.uuid,
                setCode: card.setCode,
                name: card.name,
                finish: entry?.finish || (card.finishes?.[0] || 'nonfoil'),
                game: 'magic',
            }));

            const submitData = {
                ownerTrade,
                otherTrade,
                ownerSub: true,
                otherSub: true,
                note: noteText.trim() || null,
            };

            let res;
            if (isEditing && editData?.tradeId) {
                res = await fetch(`/api/trade-requests/${editData.tradeId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(submitData)
                });
            } else if (requestData && isCounterOffer) {
                const counterOfferData = { ownerTrade, otherTrade, ownerSub: true, otherSub: true, note: noteText.trim() || null };
                res = await fetch(`/api/trade-requests/${requestData.tradeId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(counterOfferData)
                });
            } else if (requestData) {
                const responseData = {
                    tradeId: requestData.tradeId,
                    ownerId: user.id,
                    otherId: ownerId || requestData.ownerId,
                    ownerTrade,
                    otherTrade,
                    ownerSub: true,
                    otherSub: true,
                    note: noteText.trim() || null,
                };
                res = await fetch('/api/trade-requests', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(responseData)
                });
            } else if (ownerId) {
                const outgoingRequestData = {
                    ownerId: user.id,
                    otherId: ownerId,
                    ownerTrade,
                    otherTrade,
                    ownerSub: true,
                    otherSub: true,
                    note: noteText.trim() || null,
                };
                res = await fetch('/api/trade-requests', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(outgoingRequestData)
                });
            } else {
                // No partner context: offers always go TO someone -
                // the public posted-trade board is retired
                throw new Error('Pick a trader first - open their profile or a match panel and hit "Send trade".');
            }

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || `Failed to ${isEditing ? 'update' : 'submit'} request`);
            }

            setLeftTradeCards([]);
            setRightTradeCards([]);
            setNoteText('');
            setNoteCharCount(0);
            setShowPopup(true);
            setTimeout(() => {
                setShowPopup(false);
                // Land on the trade dashboard instead of leaving the
                // user sitting on the builder
                navigate('/requests');
            }, 1500);
        } catch (err) {
            setSubmitError(err.message || `Failed to ${isEditing ? 'update' : 'submit'} request.`);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Members only, and always WITH someone: the public posted-trade
    // board is retired, so a builder with no partner has nowhere to send
    if (!userLoading && !user) {
        return <LoginGate title="Trade" message="Log in to build and send trade offers." />;
    }
    if (!ownerId && !requestData) {
        return <PartnerPicker />;
    }

    if (partnerBlocked) {
        return (
            <div className="make-request-container">
                <div className="requests-placeholder" style={{ marginTop: '2rem' }}>
                    <p style={{ margin: '0 0 0.5rem' }}>{partnerBlocked}</p>
                    <p style={{ margin: 0 }}>Trades can't be sent to them.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="make-request-container">
            {/* Trade Partner Header */}
            {tradePartner && (
                <div className="trade-partner-header">
                    <AvatarFrame user={tradePartner}>
                        <img
                            src={getProfilePhotoSrc(tradePartner)}
                            alt={`${tradePartner.username}'s profile`}
                            className="trade-partner-avatar"
                            onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_DATA_URI; }}
                        />
                    </AvatarFrame>
                    <span className="trade-partner-name">Trading with {tradePartner.username}</span>
                </div>
            )}

            {/* Trade Draft Area */}
            <div className="trade-draft-area">
                <div className="trade-draft-side trade-draft-left">
                    {leftTradeCards.length === 0 ? (
                        <div 
                            className="trade-draft-card-placeholder clickable"
                            onClick={() => handlePlaceholderClick('have')}
                            title="Click to add cards from your collection"
                        >
                            <span className="placeholder-plus">+</span>
                        </div>
                    ) : (
                        [...leftTradeCards].reverse().map((item, idx) => (
                            <CondensedTradeCard
                                key={item.card.uuid + (item.entry?.finish || '') + '-' + idx}
                                card={item.card}
                                entry={item.entry}
                                onDelete={() => setLeftTradeCards(prev => prev.filter((_, i) => i !== (leftTradeCards.length - 1 - idx)))}
                                onAnyFinishClick={handleAnyFinishCardClick}
                                isHighlighted={isMatchedCard({ card: item.card, binder: item.entry })}
                            />
                        ))
                    )}
                </div>
                <div className="trade-draft-arrow">
                    <span className="trade-draft-arrow-thick">→</span>
                </div>
                <div className="trade-draft-side trade-draft-right">
                    {rightTradeCards.length === 0 ? (
                        <div 
                            className="trade-draft-card-placeholder clickable"
                            onClick={() => handlePlaceholderClick('want')}
                            title="Click to add cards you want from them"
                        >
                            <span className="placeholder-plus">+</span>
                        </div>
                    ) : (
                        [...rightTradeCards].reverse().map((item, idx) => (
                            <CondensedTradeCard
                                key={item.card.uuid + (item.entry?.finish || '') + '-' + idx}
                                card={item.card}
                                entry={item.entry}
                                onDelete={() => setRightTradeCards(prev => prev.filter((_, i) => i !== (rightTradeCards.length - 1 - idx)))}
                                onAnyFinishClick={handleAnyFinishCardClick}
                                isHighlighted={isMatchedCard({ card: item.card, binder: item.entry })}
                            />
                        ))
                    )}
                </div>
            </div>

            {/* Controls Bar */}
            <div className="trade-draft-controls-bar" ref={controlsBarRef}>
                <div className="trade-draft-controls-row">
                    <button className="trade-draft-add-note-btn" onClick={() => setShowNotePopup(true)}>
                        Add Note
                    </button>
                </div>
                <button
                    className="trade-draft-submit-btn"
                    onClick={handleSubmitRequest}
                    disabled={submitLoading || leftTradeCards.length === 0 || rightTradeCards.length === 0 || isSubmitDisabled()}
                >
                    {submitLoading ? (isEditing ? 'Updating...' : 'Submitting...') : (isEditing ? 'Update Request' : 'Submit Request')}
                </button>
                {showPopup && <div className="trade-request-popup">Trade request submitted!</div>}
            </div>
            {submitError && <div style={{ color: 'red', textAlign: 'center', marginTop: 8 }}>{submitError}</div>}

            {/* Note Popup */}
            {showNotePopup && (
                <div className="note-popup-overlay" onClick={() => setShowNotePopup(false)}>
                    <div className="note-popup" onClick={e => e.stopPropagation()}>
                        <div className="note-popup-header">
                            <h3>Add Note</h3>
                            <button className="note-popup-close" onClick={() => setShowNotePopup(false)}>×</button>
                        </div>
                        <textarea
                            className="note-textarea"
                            placeholder="Enter your note here (optional)..."
                            value={noteText}
                            onChange={handleNoteChange}
                            maxLength={250}
                            rows={4}
                        />
                        <div className="note-char-count">{noteCharCount}/250</div>
                        <div className="note-popup-actions">
                            <button className="note-cancel-btn" onClick={() => setShowNotePopup(false)}>Cancel</button>
                            <button className="note-confirm-btn" onClick={() => setShowNotePopup(false)}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tabs Bar */}
            <div className="make-request-tabs-bar" ref={searchAreaRef}>
                <button
                    className={`make-request-tab${activeTab === 'have' ? ' active' : ''}`}
                    onClick={() => setActiveTab('have')}
                >
                    Your Collection
                </button>
                <button
                    className={`make-request-tab${activeTab === 'want' ? ' active' : ''}`}
                    onClick={() => setActiveTab('want')}
                >
                    Their Collection
                </button>
            </div>

            {/* SearchFilterBar Widget - handles everything! */}
            <div className="make-request-search-content">
                {loading ? (
                    <div className="make-request-placeholder">Loading your cards...</div>
                ) : error ? (
                    <div className="make-request-placeholder" style={{ color: 'red' }}>{error}</div>
                ) : (
                    <SearchFilterBar
                        key={`${activeTab}-${effectiveToggleOn}`}
                        showCardDisplay={true}
                        dualMode={!(activeTab === 'want' && ownerId)}
                        toggleOn={effectiveToggleOn}
                        onToggleChange={(on, q) => { setPendingSearch(on ? null : (q || null)); setToggleOn(on); }}
                        mode={effectiveToggleOn ? "filter" : "search"}
                        externalCards={effectiveToggleOn ? cards : []}
                        cardMode="trade"
                        initialFilters={!effectiveToggleOn && pendingSearch ? { name: pendingSearch } : {}}
                        autoSearchOnMount={!effectiveToggleOn && !!pendingSearch}
                        filterSetsToShow={effectiveToggleOn ? availableSetsForCards : null}
                        matchedCardKeys={matchedCardKeys}
                        onAddToTrade={handleAddToTrade}
                        onAddToTradeWithFinish={handleAddToTradeWithFinish}
                        getAddState={(card, entry) => {
                            if (!entry?.quantity) return { disabled: false };
                            const list = activeTab === 'have' ? leftTradeCards : rightTradeCards;
                            return { disabled: draftCount(list, card, entry) >= (entry.quantity || 1) };
                        }}
                        activeTab={activeTab}
                        requireFilters={!effectiveToggleOn}
                        emptyTitle={effectiveToggleOn ? "No Cards" : "Search All Cards"}
                        emptyMessage={effectiveToggleOn
                            ? (activeTab === 'have' ? "You don't have any cards in your collection yet." : "They don't have any cards in their collection.")
                            : "Use the search to find cards to add to your trade."}
                        extraLeftContent={effectiveToggleOn && ownerId && (
                            <label className="toggle-switch-label">
                                <input
                                    type="checkbox"
                                    className="toggle-switch-input"
                                    checked={showMatches}
                                    onChange={e => setShowMatches(e.target.checked)}
                                />
                                <span className="toggle-switch-slider"></span>
                                <span className="toggle-switch-c">
                                    <span className="toggle-switch-text-desktop">Show Matches</span>
                                    <span className="toggle-switch-text-mobile">M</span>
                                </span>
                            </label>
                        )}
                    />
                )}
            </div>

            {/* Login Popup */}
            {showLoginPopup && (
                <div className="login-required-popup-overlay" onClick={() => setShowLoginPopup(false)}>
                    <div className="login-required-popup" onClick={e => e.stopPropagation()}>
                        <button className="login-required-popup-close" onClick={() => setShowLoginPopup(false)}>×</button>
                        <div className="login-required-popup-message">
                            You need to log in to submit a trade request.
                        </div>
                        <button className="login-required-popup-auth-btn" onClick={() => navigate('/auth')}>
                            Log In / Sign Up
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Trade;
